/**
 * Pure household-economy rules shared by the server (authoritative) and the web app (previews).
 * Nothing in here touches the database, so it is easy to unit test.
 */
import { dayInMask, type LocalParts, parseHHMM, formatHHMM, describeDays, ALL_DAYS } from "./time";
import { formatDuration } from "./duration";

export type RewardKind = "cash" | "time" | "item" | "digital" | "privilege";

export interface FloorPolicy {
  allowNegative: boolean;
  negativeFloor: number;
}

export interface KidFloorOverride {
  allowNegativeOverride: boolean | null;
  negativeFloorOverride: number | null;
}

/** The lowest balance a kid may reach: 0, or a parent-set negative floor such as -50. */
export function effectiveFloor(family: FloorPolicy, kid?: KidFloorOverride | null): number {
  const allow = kid?.allowNegativeOverride ?? family.allowNegative;
  if (!allow) return 0;
  const floor = kid?.negativeFloorOverride ?? family.negativeFloor;
  return Math.min(0, Math.trunc(floor));
}

/** How many points can be deducted before hitting the floor. */
export function maxDeductible(balance: number, floor: number): number {
  return Math.max(0, balance - floor);
}

export interface RewardTypeLike {
  kind: RewardKind;
  pointsPerUnit: number | null;
  unitLabel: string;
  name?: string;
}

/** Reward types whose converted units are held in a per-kid bank until used (screen time, etc). */
export function isBanked(kind: RewardKind): boolean {
  return kind === "time";
}

/** Reward types kids can convert points into directly (as opposed to buying store items). */
export function isConvertible(rt: RewardTypeLike): boolean {
  return (rt.kind === "cash" || rt.kind === "time") && !!rt.pointsPerUnit && rt.pointsPerUnit > 0;
}

export function pointsForUnits(units: number, pointsPerUnit: number): number {
  return Math.ceil(round2(units * pointsPerUnit));
}

export function unitsForPoints(points: number, pointsPerUnit: number): number {
  if (!pointsPerUnit) return 0;
  return round2(points / pointsPerUnit);
}

export function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

export interface DeductionPlan {
  /** Units removed from the reward bank (e.g. minutes of saved screen time). */
  bankUnits: number;
  /** Points removed from the general balance. */
  points: number;
  /** Points that would have been removed without the floor. */
  pointsRequested: number;
  /** True when the floor stopped part of the deduction. */
  clampedByFloor: boolean;
}

/**
 * Plan a deduction.
 *  - Targeting the general balance removes `amount` points.
 *  - Targeting a banked reward (screen time) removes saved units first, then converts any
 *    remainder to points at the family rate.
 *  - Targeting a cash reward converts dollars to points.
 * Points never push a balance below the kid's floor.
 */
export function planDeduction(input: {
  amount: number;
  balance: number;
  floor: number;
  rewardType?: RewardTypeLike | null;
  bankBalance?: number;
}): DeductionPlan {
  const amount = Math.max(0, input.amount);
  let bankUnits = 0;
  let pointsRequested: number;
  const rt = input.rewardType;
  if (!rt) {
    pointsRequested = Math.ceil(amount);
  } else {
    const ppu = rt.pointsPerUnit ?? 1;
    let remainingUnits = amount;
    if (isBanked(rt.kind)) {
      bankUnits = round2(Math.min(Math.max(0, input.bankBalance ?? 0), amount));
      remainingUnits = round2(amount - bankUnits);
    }
    pointsRequested = remainingUnits > 0 ? pointsForUnits(remainingUnits, ppu) : 0;
  }
  const points = Math.min(pointsRequested, maxDeductible(input.balance, input.floor));
  return { bankUnits, points, pointsRequested, clampedByFloor: points < pointsRequested };
}

/** Work-it-off credits only dig a kid out of a negative balance; they never create surplus. */
export function recoveryCredit(balance: number, amount: number): number {
  if (balance >= 0) return 0;
  return Math.max(0, Math.min(Math.trunc(amount), -balance));
}

export interface ScheduleRulesLike {
  allowedDays: number;
  startTime?: string | null;
  endTime?: string | null;
  note?: string | null;
}

export function checkSchedule(schedule: ScheduleRulesLike | null | undefined, local: LocalParts) {
  if (!schedule) return { allowed: true as const, reason: null };
  if (!dayInMask(schedule.allowedDays ?? ALL_DAYS, local.dow)) {
    return {
      allowed: false as const,
      reason: schedule.note || `Only allowed on: ${describeDays(schedule.allowedDays)}`,
    };
  }
  const start = parseHHMM(schedule.startTime);
  const end = parseHHMM(schedule.endTime);
  if (start !== null && local.minutes < start) {
    return { allowed: false as const, reason: schedule.note || `Not before ${formatHHMM(schedule.startTime)}` };
  }
  if (end !== null && local.minutes >= end) {
    return { allowed: false as const, reason: schedule.note || `Not after ${formatHHMM(schedule.endTime)}` };
  }
  return { allowed: true as const, reason: null };
}

export function describeSchedule(schedule: ScheduleRulesLike | null | undefined): string | null {
  if (!schedule) return null;
  const parts: string[] = [];
  if (schedule.allowedDays !== ALL_DAYS) parts.push(describeDays(schedule.allowedDays));
  if (schedule.startTime && schedule.endTime) {
    parts.push(`${formatHHMM(schedule.startTime)}–${formatHHMM(schedule.endTime)}`);
  } else if (schedule.endTime) {
    parts.push(`Until ${formatHHMM(schedule.endTime)}`);
  } else if (schedule.startTime) {
    parts.push(`After ${formatHHMM(schedule.startTime)}`);
  }
  if (schedule.note) parts.push(schedule.note);
  return parts.length ? parts.join(" · ") : null;
}

export function checkCap(used: number, requested: number, cap: number | null | undefined) {
  if (cap === null || cap === undefined) return { allowed: true, remaining: null as number | null };
  const remaining = round2(Math.max(0, cap - used));
  return { allowed: requested <= remaining + 1e-9, remaining };
}

export function formatUnits(units: number, rt: Pick<RewardTypeLike, "kind" | "unitLabel">): string {
  if (rt.kind === "cash") {
    const symbol = rt.unitLabel && rt.unitLabel.length <= 2 ? rt.unitLabel : "$";
    return `${symbol}${units.toFixed(2)}`;
  }
  if (rt.kind === "time") return formatDuration(units);
  return `${round2(units)} ${rt.unitLabel}`;
}

/** Due-ness of a responsibility on the given local day. */
export function isResponsibilityDue(
  r: { frequency: "daily" | "weekly" | "once"; daysOfWeek: number },
  local: LocalParts,
): boolean {
  if (r.frequency === "daily") return dayInMask(r.daysOfWeek, local.dow);
  return true;
}

/** Whether a completion status satisfies the gate under the family's verification mode. */
export function completionCounts(status: "done" | "approved" | "rejected" | null | undefined, mode: "trust" | "verify") {
  if (status === "approved") return true;
  if (status === "done") return mode === "trust";
  return false;
}

export function ageFromBirthYear(birthYear: number | null | undefined, now = Date.now()): number | null {
  if (!birthYear) return null;
  return new Date(now).getUTCFullYear() - birthYear;
}
